import { CircleHelp, LogOut, MessageSquare, MessageSquarePlus, PanelLeft, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { ConversationHistoryStatus, StoredConversation } from "@/hooks/useChat";
import { isEmbeddedInHub } from "@/lib/platformAuth";

interface SidebarProps {
  isOpen: boolean;
  currentConversationId: string;
  savedConversations: StoredConversation[];
  conversationHistoryStatus: ConversationHistoryStatus;
  onRetryConversationHistory: () => void;
  onNewChat: () => void;
  onToggleSidebar: () => void;
  onLoadConversation: (conv: StoredConversation) => void;
  onDeleteConversation: (id: string) => void;
  onHelp: () => void;
  onFeedback: () => void;
  /** Web / Platform only — omit inside Teams. */
  onSignOut?: () => void;
  signedInEmail?: string | null;
}

type Group = { label: string; items: StoredConversation[] };

function groupConversations(convs: StoredConversation[]): Group[] {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOf7Days = new Date(startOfToday);
  startOf7Days.setDate(startOf7Days.getDate() - 7);
  const startOf30Days = new Date(startOfToday);
  startOf30Days.setDate(startOf30Days.getDate() - 30);

  const groups: Group[] = [
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "Last 7 days", items: [] },
    { label: "Last 30 days", items: [] },
    { label: "Older", items: [] },
  ];

  for (const conv of convs) {
    const ts = new Date(conv.updatedAt).getTime();
    if (ts >= startOfToday.getTime()) groups[0].items.push(conv);
    else if (ts >= startOfYesterday.getTime()) groups[1].items.push(conv);
    else if (ts >= startOf7Days.getTime()) groups[2].items.push(conv);
    else if (ts >= startOf30Days.getTime()) groups[3].items.push(conv);
    else groups[4].items.push(conv);
  }

  return groups.filter((g) => g.items.length > 0);
}

export function Sidebar({
  isOpen,
  currentConversationId,
  savedConversations,
  conversationHistoryStatus,
  onRetryConversationHistory,
  onNewChat,
  onToggleSidebar,
  onLoadConversation,
  onDeleteConversation,
  onHelp,
  onFeedback,
  onSignOut,
  signedInEmail,
}: SidebarProps) {
  const groups = groupConversations(savedConversations);
  const inHub = isEmbeddedInHub();
  const historyFailed =
    conversationHistoryStatus === "server-error" ||
    conversationHistoryStatus === "network-error";

  return (
    // The aside controls width for the slide animation.
    // bg-surface + rounded-r-2xl creates the floating panel effect on the bg-bg canvas.
    // overflow-hidden clips children to the rounded shape during the transition.
    <aside
      className={`h-full flex-shrink-0 overflow-hidden rounded-2xl bg-surface shadow-[0_2px_16px_rgba(15,26,46,0.07)] transition-[width] duration-200 ease-in-out ${
        isOpen ? "w-72" : "w-0"
      }`}
      aria-label="Navigation sidebar"
      aria-hidden={!isOpen}
    >
      <div className="flex h-full w-72 flex-col">

        {/* Header: NDC logo + Assistant/Beta + collapse. Hub already has the NDC logo. */}
        <div className="flex h-14 items-center justify-between gap-2 px-4">
          <div className="flex min-w-0 items-center gap-2">
            {!inHub ? (
              <>
                <img
                  src="/ndc-logo.svg"
                  alt="National Datacare"
                  className="h-6 w-auto shrink-0 select-none dark:hidden"
                  draggable={false}
                />
                <img
                  src="/ndc-logo-white.svg"
                  alt=""
                  className="hidden h-6 w-auto shrink-0 select-none dark:block"
                  draggable={false}
                />
              </>
            ) : null}
            <span className="shrink-0 text-[11px] font-medium text-text-muted">Assistant</span>
            <span className="shrink-0 rounded-full border border-border bg-surface-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text">
              Beta
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            aria-label="Close navigation"
            title="Close navigation"
            className="h-7 w-7 shrink-0"
          >
            <PanelLeft className="h-[16px] w-[16px]" />
          </Button>
        </div>

        {/* New Chat button */}
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={onNewChat}
            aria-label="Start a new chat"
            className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-[13px] font-medium text-text-muted transition-colors hover:border-ndc-red hover:bg-[var(--ndc-red-tint)] hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red"
          >
            <Plus className="h-3.5 w-3.5 shrink-0" />
            New Chat
          </button>
        </div>

        {/* Conversation history */}
        <div className="flex-1 overflow-y-auto px-2 py-1">
          {conversationHistoryStatus === "loading" && savedConversations.length === 0 ? (
            <p className="px-2 py-1 text-[12px] leading-relaxed text-text-muted" role="status">
              Loading your conversations…
            </p>
          ) : conversationHistoryStatus === "unauthenticated" && savedConversations.length === 0 ? (
            <HistoryUnavailable
              message="Sign in again to load your conversations."
              onRetry={onRetryConversationHistory}
            />
          ) : historyFailed && savedConversations.length === 0 ? (
            <HistoryUnavailable
              message="Conversation history is temporarily unavailable. Your conversations have not been deleted."
              onRetry={onRetryConversationHistory}
            />
          ) : savedConversations.length === 0 ? (
            <p className="px-2 py-1 text-[12px] leading-relaxed text-text-muted">
              Your conversations will appear here.
            </p>
          ) : (
            <div className="space-y-4">
              {(historyFailed || conversationHistoryStatus === "unauthenticated") && (
                <HistoryUnavailable
                  message="We couldn't refresh your conversations. Showing the last available list."
                  onRetry={onRetryConversationHistory}
                />
              )}
              {groups.map((group) => (
                <div key={group.label}>
                  <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted opacity-50">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((conv) => {
                      const isActive = conv.id === currentConversationId;
                      return (
                        <div
                          key={conv.id}
                          className={`group flex items-center gap-1 rounded-lg px-2 py-[7px] text-[13px] transition-colors ${
                            isActive
                              ? "bg-surface-muted text-text"
                              : "text-text-muted hover:bg-surface-muted hover:text-text"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => onLoadConversation(conv)}
                            className="flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none"
                            aria-current={isActive ? "true" : undefined}
                            title={conv.title}
                          >
                            <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-50" />
                            <span className="truncate">{conv.title}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteConversation(conv.id)}
                            aria-label={`Delete conversation: ${conv.title}`}
                            className="shrink-0 rounded p-0.5 text-text-muted opacity-0 transition-opacity hover:text-[var(--danger)] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ndc-red group-hover:opacity-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer: Help + Feedback + Sign out (web only) */}
        <div className="border-t border-border/60 px-3 py-3 space-y-0.5">
          <button
            type="button"
            onClick={onHelp}
            aria-label="Help and feature guide"
            className="flex w-full items-center gap-2 rounded-lg px-2 py-[7px] text-[13px] text-text-muted transition-colors hover:bg-surface-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red"
          >
            <CircleHelp className="h-3.5 w-3.5 shrink-0 opacity-60" />
            Help & features
          </button>
          <button
            type="button"
            onClick={onFeedback}
            aria-label="Share feedback"
            className="flex w-full items-center gap-2 rounded-lg px-2 py-[7px] text-[13px] text-text-muted transition-colors hover:bg-surface-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red"
          >
            <MessageSquarePlus className="h-3.5 w-3.5 shrink-0 opacity-60" />
            Share feedback
          </button>
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              aria-label="Sign out"
              className="flex w-full items-center gap-2 rounded-lg px-2 py-[7px] text-[13px] text-text-muted transition-colors hover:bg-surface-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red"
            >
              <LogOut className="h-3.5 w-3.5 shrink-0 opacity-60" />
              Sign out
            </button>
          ) : null}
          {signedInEmail ? (
            <p className="truncate px-2 pt-1 text-[11px] leading-relaxed text-text-muted opacity-50" title={signedInEmail}>
              {signedInEmail}
            </p>
          ) : (
            <p className="px-2 pt-1 text-[11px] leading-relaxed text-text-muted opacity-50">
              Conversations are saved to your account.
            </p>
          )}
        </div>
      </div>
    </aside>
  );
}

function HistoryUnavailable({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="mx-1 mb-3 rounded-lg border border-border bg-surface-muted p-2.5" role="alert">
      <p className="text-[12px] leading-relaxed text-text-muted">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 flex items-center gap-1.5 rounded text-[12px] font-medium text-text hover:text-ndc-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red"
      >
        <RefreshCw className="h-3 w-3" />
        Retry
      </button>
    </div>
  );
}
